import React, { FC, useState, type PropsWithChildren } from "react";
import AccordionSection from "./AccordionSection";
import { Switch } from "../../../../../components";
import { cn } from "../../../../../components/utils/cn";
import type { ProfileLinkConfig } from "../../../../../client";

const SettingRow: FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="flex items-center justify-between gap-2">{children}</div>
  );
};

const SettingText: FC<PropsWithChildren> = ({ children }) => {
  return <p className="text-gray-9 text-sm font-medium grow">{children}</p>;
};

interface SkillStackLinkSettingsProps {
  disabled?: boolean;
  linkConfig?: ProfileLinkConfig;
  onUpdate: (payload: Partial<ProfileLinkConfig>) => void;
  /** Hide the top/bottom placement toggle — in the template flow the template
   *  owns skill placement, so this control isn't relevant there. */
  hidePlacement?: boolean;
  /** Replace the legacy "Sorted" toggle with a Layout selector (Skill cloud /
   *  Lista). Only the template editor opts in; the legacy side menu keeps the
   *  old toggle so its rendering is unchanged. */
  useLayoutSetting?: boolean;
}

const SkillStackLinkSettings: FC<SkillStackLinkSettingsProps> = ({
  disabled,
  linkConfig,
  onUpdate,
  hidePlacement,
  useLayoutSetting,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const asList = linkConfig?.skillsSummaryConfig?.displayAsList === true;
  const order =
    (linkConfig?.skillsSummaryConfig as { skillOrder?: string } | undefined)
      ?.skillOrder ?? "";

  return (
    <AccordionSection
      title="Skill stack"
      subtitle={(() => {
        const activeSettings: string[] = [];
        if (!hidePlacement) {
          if (linkConfig?.skillsSummaryConfig?.placeInTop) {
            activeSettings.push("Place in top");
          } else {
            activeSettings.push("Place in bottom");
          }
        }
        if (linkConfig?.skillsSummaryConfig?.sortedMode === "basic") {
          activeSettings.push("sorted");
        } else {
          activeSettings.push("not sorted");
        }
        if (linkConfig?.skillsSummaryConfig?.displayIcons === true) {
          activeSettings.push("display icons");
        } else {
          activeSettings.push("no icons");
        }
        if (activeSettings.length === 0) {
          return "No settings active";
        }
        return activeSettings.join(", ");
      })()}
      expanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
    >
      {!hidePlacement && (
        <SettingRow>
          <Switch
            checked={linkConfig?.skillsSummaryConfig?.placeInTop}
            disabled={disabled}
            onCheckedChange={(checked) => {
              if (typeof checked !== "boolean") return;

              onUpdate({
                skillsSummaryConfig: {
                  ...linkConfig?.skillsSummaryConfig,
                  placeInTop: checked,
                },
              });
            }}
          />
          <SettingText>Place in top</SettingText>
        </SettingRow>
      )}
      {useLayoutSetting ? (
        <div className="flex flex-col gap-1.5">
          <SettingText>Layout</SettingText>
          <div className="flex flex-row gap-1 rounded-lg bg-gray-2 p-1">
            {[
              { value: false, label: "Compact" },
              { value: true, label: "List" },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                disabled={disabled}
                onClick={() =>
                  onUpdate({
                    skillsSummaryConfig: {
                      ...linkConfig?.skillsSummaryConfig,
                      displayAsList: opt.value,
                      // Cloud is the flat layout — clear the legacy categorised
                      // grouping so it doesn't override the choice.
                      ...(opt.value ? {} : { sortedMode: "" }),
                    },
                  })
                }
                className={cn(
                  "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  asList === opt.value
                    ? "bg-white text-black shadow-sm"
                    : "text-gray-6 hover:text-black",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <SettingRow>
          <Switch
            checked={linkConfig?.skillsSummaryConfig?.sortedMode === "basic"}
            disabled={disabled}
            onCheckedChange={(checked) => {
              if (typeof checked !== "boolean") return;

              onUpdate({
                skillsSummaryConfig: {
                  ...linkConfig?.skillsSummaryConfig,
                  sortedMode: checked ? "basic" : "",
                },
              });
            }}
          />
          <SettingText>Sorted</SettingText>
        </SettingRow>
      )}
      {useLayoutSetting && (
        <div className="flex flex-col gap-1.5">
          <SettingText>Order</SettingText>
          <div className="flex flex-row gap-1 rounded-lg bg-gray-2 p-1">
            {[
              { value: "", label: "Alphabetical" },
              { value: "mostMentioned", label: "Most mentioned" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() =>
                  onUpdate({
                    skillsSummaryConfig: {
                      ...linkConfig?.skillsSummaryConfig,
                      skillOrder: opt.value,
                    },
                  } as Partial<ProfileLinkConfig>)
                }
                className={cn(
                  "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  order === opt.value
                    ? "bg-white text-black shadow-sm"
                    : "text-gray-6 hover:text-black",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <SettingRow>
        <Switch
          checked={linkConfig?.skillsSummaryConfig?.displayIcons === true}
          disabled={disabled}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            onUpdate({
              skillsSummaryConfig: {
                ...linkConfig?.skillsSummaryConfig,
                displayIcons: checked,
              },
            });
          }}
        />
        <SettingText>Display icons</SettingText>
      </SettingRow>
    </AccordionSection>
  );
};

export default SkillStackLinkSettings;
